import {
	createClient,
	type JiraProject,
	jiraProjectsQuery,
	JiraScope,
} from '@atlassian/dynamic-cards-search-client';

import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters';
import {
	type FetchedSelectFilterOption,
	type SelectFilterMonad,
	type SelectFilterOption,
} from '../../../../filters/base-select-filter/types';
import { type CloudConfig } from '../../../../filters/types';

const projectIdToARI = (cloudId: string, projectId: string) =>
	`ari:cloud:jira:${cloudId}:project/${projectId}`;

const searchProject = async (query: string, config: CloudConfig) => {
	const searchClient = createClient({
		cloudId: config.cloudId || '',
	});

	const searchProjectsResponse = await searchClient.search({
		query,
		resultLimit: 5,
		scopes: [JiraScope.JiraProject],
		analytics: {
			identifier: 'search-page',
		},
	});

	const searchedProjects: JiraProject[] =
		searchProjectsResponse.response.rawData.scopes.length > 0
			? searchProjectsResponse.response.rawData.scopes[0].results.map((projectResult) => ({
					projectId: projectResult.id,
					name: projectResult.name,
					avatar: {
						small:
							'url' in projectResult.attributes.avatar
								? projectResult.attributes.avatar.url
								: undefined,
					},
				}))
			: [];

	const allProjects: JiraProject[] = searchedProjects;

	const projectOptions = allProjects.map(
		(project) =>
			({
				trackingKey: projectIdToARI(config.cloudId || '', project.projectId),
				value: projectIdToARI(config.cloudId || '', project.projectId),
				label: project.name,
				avatarUrl: project.avatar?.small,
				queryParamValue: project.projectId,
			}) satisfies FetchedSelectFilterOption,
	);
	return projectOptions;
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];

const originalProducts3P: ProductKeys3P[] = [];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('project');

const getProducts3P = () => {
	// fg('rovo-full-page-search-3p-static-config') => false
	return false ? products3PFromConfig : originalProducts3P;
};

export const filter: {
	customValueFields: string[] | undefined;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'project'>;
	id: 'project';
	isCompatible: (
		filterState?:
			| {
					id: string;
			  }
			| undefined,
	) => filterState is Readonly<{
		alwaysVisible: boolean;
		customValue?: Record<string, string>;
		customValueFields?: string[];
		didMount: boolean;
		hasSelection: boolean;
		id: 'project';
		initialOptions: {
			isLoading: boolean;
			options: SelectFilterOption[];
		};
		lookup: {
			input: string;
			isActive: boolean;
			isDefined: boolean;
			isLoading: boolean;
			lookupResultOptions: SelectFilterOption[];
			options: SelectFilterOption[];
		};
		options: SelectFilterOption[];
		products: ProductKeys[];
		queryParams: {
			isLoading: boolean;
			options: SelectFilterOption[];
		};
		selectedOptions: SelectFilterOption[];
		type: 'single-select' | 'multi-select';
		universal: boolean;
		value: string[];
	}>;
	isSupportCustomValue: boolean;
	products: ProductKeys[];
	queryParams: {
		key: string;
	};
	type: 'multi-select';
	universal: boolean;
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'project'>;
} = createMultiSelectFilter({
	id: 'project',
	products: [...productKeys1P, ...getProducts3P()],
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'jProject',
		fetcher: async (queryParams, config) => {
			const initialProjectARIs = queryParams.map((projectId) =>
				projectIdToARI(config.cloudId || '', projectId),
			);
			const initialProjectsResponse = await jiraProjectsQuery(initialProjectARIs);

			const initialProjects: JiraProject[] = initialProjectsResponse.data?.jira.jiraProjects ?? [];

			const allProjects: JiraProject[] = initialProjects;
			const projectOptions = allProjects.map(
				(project) =>
					({
						trackingKey: projectIdToARI(config.cloudId || '', project.projectId),
						value: projectIdToARI(config.cloudId || '', project.projectId),
						label: project.name,
						avatarUrl: project.avatar?.small,
						queryParamValue: project.projectId,
					}) satisfies FetchedSelectFilterOption,
			);
			return projectOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchProject('', config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchProject(query, config);
		},
	},
});
